/**
 * MapLibre Layer Style Definitions for UnifiedAnchorSymbolLayer
 *
 * Defines expression-based styles for 2 marker layers + 2 cluster layers:
 * 1. Cluster circles — aggregated task groups
 * 2. Cluster count — text labels showing cluster size
 * 3. Marker border — outline effect (shape sprite, slightly larger)
 * 4. Marker composite — shape with icon cutout (single layer, no z-order bleed)
 *
 * All layers use the same GeoJSON source with cluster/unclustered filters.
 *
 * @module components/map/UnifiedAnchorSymbolLayer/styles/symbolLayerStyles
 */

import type {
  CircleLayerSpecification,
  SymbolLayerSpecification,
  FilterSpecification,
  ExpressionSpecification,
} from '@maplibre/maplibre-gl-style-spec';
import { MarkerShape } from '@/types';
import {
  ZOOM_SCALE_MIN,
  ZOOM_SCALE_MAX,
  SCALE_MIN as SCALE_FACTOR_MIN,
  SCALE_MAX as SCALE_FACTOR_MAX,
} from '@/utils/markerScaling';
import { SHAPE_SPRITE_SIZE } from '../../symbolSprites';
import { MARKER_CLUSTER_RADIUS } from '@/constants';
import { SYMBOL_LAYER_COLORS } from '@/constants/colorPresets';
import {
  MAP_CLUSTER_FEATURE_KIND_TASK,
  buildUnclusteredFeatureFilter,
} from '../../MapClusterLayer/featureKind';

// ===== LAYER & SOURCE IDENTIFIERS =====

export const LAYER_IDS = {
  CLUSTER_CIRCLES: 'task-cluster-circles',
  CLUSTER_COUNT: 'task-cluster-count',
  MARKER_BORDER: 'task-marker-border',
  MARKER_COMPOSITE: 'task-marker-composite',
} as const;

// ===== FILTER EXPRESSIONS =====

const clusterFilter: FilterSpecification = ['has', 'point_count'];

/**
 * Excludes notes and tasks without a Marker geometry (Line/Shape only,
 * `hasMarker === false`). Marker-less tasks still inflate cluster counts;
 * their geometries are drawn by `TaskGeometryOverlays`.
 */
const unclusteredTaskMarkerFilter = buildUnclusteredFeatureFilter(
  MAP_CLUSTER_FEATURE_KIND_TASK,
  ['get', 'hasMarker']
);

// ===== CLUSTER SOURCE CONSTANTS =====

export const CLUSTER_RADIUS = MARKER_CLUSTER_RADIUS;
export const CLUSTER_MAX_ZOOM = 14;

// ===== CLUSTER STYLE CONSTANTS =====

const CLUSTER_RADIUS_SMALL = 18;
const CLUSTER_RADIUS_MEDIUM = 24;
const CLUSTER_RADIUS_LARGE = 30;
const CLUSTER_STROKE_WIDTH = 2;
const CLUSTER_FILL_OPACITY = 0.85;
const CLUSTER_THRESHOLD_MEDIUM = 10;
const CLUSTER_THRESHOLD_LARGE = 50;

/** Cluster fill step expressions — darker shade = larger cluster */
export const CLUSTER_COLOR_LIGHT: ExpressionSpecification = [
  'step',
  ['get', 'point_count'],
  SYMBOL_LAYER_COLORS.cluster.light.small,
  CLUSTER_THRESHOLD_MEDIUM,
  SYMBOL_LAYER_COLORS.cluster.light.medium,
  CLUSTER_THRESHOLD_LARGE,
  SYMBOL_LAYER_COLORS.cluster.light.large,
];

export const CLUSTER_COLOR_DARK: ExpressionSpecification = [
  'step',
  ['get', 'point_count'],
  SYMBOL_LAYER_COLORS.cluster.dark.small,
  CLUSTER_THRESHOLD_MEDIUM,
  SYMBOL_LAYER_COLORS.cluster.dark.medium,
  CLUSTER_THRESHOLD_LARGE,
  SYMBOL_LAYER_COLORS.cluster.dark.large,
];

const CLUSTER_STROKE_LIGHT = SYMBOL_LAYER_COLORS.cluster.strokeLight;
const CLUSTER_TEXT_LIGHT = SYMBOL_LAYER_COLORS.cluster.textLight;

// ===== CLUSTER COUNT CONSTANTS =====

const CLUSTER_COUNT_FONT: string[] = ['Open Sans Semibold'];
const CLUSTER_COUNT_FONT_SIZE = 13;

// ===== MARKER SHAPE SCALING CONSTANTS =====

/**
 * Base marker size in pixels used by the current DOM markers.
 * Used to compute icon-size so MapLibre sprites render at equivalent visual size.
 */
const MARKER_BASE_SIZE_PX = 36;

const SHAPE_SIZE_RATIO = MARKER_BASE_SIZE_PX / SHAPE_SPRITE_SIZE; // 0.5625
const SHAPE_ICON_SIZE_MIN = SCALE_FACTOR_MIN * SHAPE_SIZE_RATIO;
const SHAPE_ICON_SIZE_MAX = SCALE_FACTOR_MAX * SHAPE_SIZE_RATIO;

// ===== MARKER BORDER CONSTANTS =====

export const BORDER_COLOR_LIGHT = SYMBOL_LAYER_COLORS.border.light;
export const BORDER_COLOR_DARK = SYMBOL_LAYER_COLORS.border.dark;
const BORDER_SIZE_MULTIPLIER = 1.15;

// ===== CLUSTER CIRCLE LAYER =====

const clusterRadiusExpression: ExpressionSpecification = [
  'step',
  ['get', 'point_count'],
  CLUSTER_RADIUS_SMALL,
  CLUSTER_THRESHOLD_MEDIUM,
  CLUSTER_RADIUS_MEDIUM,
  CLUSTER_THRESHOLD_LARGE,
  CLUSTER_RADIUS_LARGE,
];

export const clusterCirclesLayer: Omit<CircleLayerSpecification, 'source'> = {
  id: LAYER_IDS.CLUSTER_CIRCLES,
  type: 'circle',
  filter: clusterFilter,
  paint: {
    'circle-color': CLUSTER_COLOR_LIGHT as unknown as string,
    'circle-radius': clusterRadiusExpression,
    'circle-stroke-width': CLUSTER_STROKE_WIDTH,
    'circle-stroke-color': CLUSTER_STROKE_LIGHT,
    'circle-opacity': CLUSTER_FILL_OPACITY,
  },
};

// ===== CLUSTER COUNT LAYER =====

export const clusterCountLayer: Omit<SymbolLayerSpecification, 'source'> = {
  id: LAYER_IDS.CLUSTER_COUNT,
  type: 'symbol',
  filter: clusterFilter,
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': CLUSTER_COUNT_FONT,
    'text-size': CLUSTER_COUNT_FONT_SIZE,
    'text-allow-overlap': true,
  },
  paint: {
    'text-color': CLUSTER_TEXT_LIGHT,
  },
};

// ===== MARKER SHAPE EXPRESSIONS =====

/** Maps MarkerShape enum to border sprite keys (plain shape, no icon cutout) */
const shapeImageExpression: ExpressionSpecification = [
  'match',
  ['get', 'shape'],
  MarkerShape.Circle,
  'shape-circle',
  MarkerShape.Square,
  'shape-square',
  MarkerShape.Triangle,
  'shape-triangle',
  'shape-circle', // fallback
];

const shapeIconSizeExpression: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  ZOOM_SCALE_MIN,
  SHAPE_ICON_SIZE_MIN,
  ZOOM_SCALE_MAX,
  SHAPE_ICON_SIZE_MAX,
];

// ===== COMPOSITE IMAGE EXPRESSION =====

/**
 * Resolves the composite sprite key from feature properties.
 * When icon is empty: 'composite-circle', 'composite-square', 'composite-triangle'
 * When icon is set: 'composite-circle-road', 'composite-triangle-hazard-warning', etc.
 *
 * Uses concat + downcase expressions to build the key dynamically,
 * avoiding a 42-entry match table.
 */
const compositeImageExpression: ExpressionSpecification = [
  'case',
  ['==', ['get', 'icon'], ''],
  // No icon → composite-{shape}
  ['concat', 'composite-', ['downcase', ['get', 'shape']]],
  // With icon → composite-{shape}-{icon}
  [
    'concat',
    'composite-',
    ['downcase', ['get', 'shape']],
    '-',
    ['get', 'icon'],
  ],
];

// ===== BORDER LAYER =====

const borderIconSizeExpression: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  ZOOM_SCALE_MIN,
  SHAPE_ICON_SIZE_MIN * BORDER_SIZE_MULTIPLIER,
  ZOOM_SCALE_MAX,
  SHAPE_ICON_SIZE_MAX * BORDER_SIZE_MULTIPLIER,
];

/** Border layer — rendered BELOW composite to create an outline effect.
 *  Also provides the color that shows through icon cutouts (white/dark). */
export const markerBorderLayer: Omit<SymbolLayerSpecification, 'source'> = {
  id: LAYER_IDS.MARKER_BORDER,
  type: 'symbol',
  filter: unclusteredTaskMarkerFilter,
  layout: {
    'icon-image': shapeImageExpression,
    'icon-size': borderIconSizeExpression,
    'icon-allow-overlap': true,
    'icon-anchor': 'center',
    'icon-padding': 0,
  },
  paint: {},
};

// ===== COMPOSITE LAYER =====

/** Composite layer — shape fill with icon cutout. Single layer eliminates z-order bleed. */
export const markerCompositeLayer: Omit<SymbolLayerSpecification, 'source'> = {
  id: LAYER_IDS.MARKER_COMPOSITE,
  type: 'symbol',
  filter: unclusteredTaskMarkerFilter,
  layout: {
    'icon-image': compositeImageExpression,
    'icon-size': shapeIconSizeExpression,
    'icon-allow-overlap': true,
    'icon-anchor': 'center',
    'icon-padding': 0,
  },
  paint: {
    'icon-color': ['get', 'color'] as ExpressionSpecification,
  },
};

// ===== CLUSTER AGGREGATION PROPERTIES =====

export const clusterProperties: Record<string, ExpressionSpecification> = {
  sumPending: ['+', ['case', ['==', ['get', 'status'], 'Pending'], 1, 0]],
  sumInProgress: ['+', ['case', ['==', ['get', 'status'], 'InProgress'], 1, 0]],
  sumDelayed: ['+', ['case', ['==', ['get', 'status'], 'Delayed'], 1, 0]],
  sumCompleted: ['+', ['case', ['==', ['get', 'status'], 'Completed'], 1, 0]],
};
